/** @typedef {import('./ProfileNavigationProps').default} Props */

import { withAuth } from '../../shared/AuthProvider';
import { translate } from '../Internationalization';
import { withSubscriptionService } from '../../service-container';
import captureError from '../../utils/captureError';
import images from '../../config/local/images';
import NavigationList from '../shared/NavigationList';
import NavigationListSignOut from './NavigationListSignOut';
import paths from '../../config/local/paths';
import React from 'react';

/** @extends {React.Component<Props>} */
class ProfileNavigationList extends React.Component {
	static defaultProps = {
		color: 'info-contrasted',
		upgradeLink: false,
	};

	/** @param {Props} props 	*/
	constructor(props) {
		super(props);

		/** @type {{subscriptionLink: string | null}} */
		this.state = {
			subscriptionLink: null,
		};
	}

	async componentDidMount() {
		if (this.#notSubscribed('agent') && this.#notSubscribed('manager')) {
			return;
		}

		const { agentSubscriptionLevels, roles } = this.props.auth;
		const isAgent = roles.includes('agent');
		const isManager = roles.includes('manager');
		const isAgentSub = isAgent && agentSubscriptionLevels.includes('agent');

		if (isManager || isAgentSub) {
			await this.#getSubscriptionManagementLink();
		}
	}

	async #getSubscriptionManagementLink() {
		try {
			const subscriptionLink =
				await this.props.subscriptionService.getAgentSubscriptionManagementLink();

			this.setState({
				subscriptionLink: subscriptionLink?.url ?? null,
			});
		} catch (error) {
			captureError(error);
		}
	}

	/**
	 * @param {string} userRole
	 * @returns {number | boolean}
	 */
	#notSubscribed(userRole) {
		return (
			this.props.upgradeLink &&
			!this.props.auth.isSubscribed &&
			this.props.auth.roles.some(
				/**
				 * @param {string} role
				 * @returns {boolean}
				 */
				(role) => [userRole].includes(role)
			)
		);
	}

	/** @returns {JSX.Element} */
	render() {
		const links = [
			{
				icon: images.icons.account,
				key: 'global-settings',
				label: translate('global.nav.account'),
				to: paths.app.profile.settings,
			},
		];

		if (this.state.subscriptionLink) {
			links.push({
				icon: images.icons.billingDetails,
				key: 'global-billing',
				label: translate('global.nav.billing_details'),
				to: this.state.subscriptionLink,
			});
		}

		links.push({
			icon: images.icons.help,
			key: 'global-help',
			label: translate('global.nav.help'),
			to: paths.help.homekeepr,
		});

		if (this.#notSubscribed('agent')) {
			links.push({
				icon: images.icons.subscription,
				key: 'agent-subscription',
				label: translate('admin.nav.main.subscription'),
				to: paths.app.agent.subscription.root,
			});
		}

		if (this.#notSubscribed('owner')) {
			links.push({
				icon: images.icons.subscription,
				key: 'owner-subscription',
				label: translate('admin.nav.main.subscription'),
				to: paths.app.admin.subscription.root,
			});
		}

		return (
			<NavigationList
				appendItem={<NavigationListSignOut color={this.props.color} />}
				color={this.props.color}
				heading={translate('global.nav.settings')}
				links={links}
				onListItemClick={this.props.onListItemClick}
			/>
		);
	}
}

// @ts-ignore
export default withAuth(withSubscriptionService(ProfileNavigationList));
